/**
 * STEP COLOURS — "paint this number by which band it falls in" (2026-08-18).
 *
 * From item 5 of the review: *"colours should be pickable, and maybe pickable
 * based on steps (ex 0-10, 20-100) and customizables"*. A count is not a category:
 * three unanswered messages and three hundred are the same tile wearing the same
 * colour today, and the severity a card was authored with cannot say which of them
 * the reader is looking at. A step list lets the NUMBER pick the colour.
 *
 * ## The shape, and why it is a list of floors rather than a list of ranges
 *
 *   [ { from: 0, color: '#…' }, { from: 10, color: '#…' }, { from: 100, color: '#…' } ]
 *
 * The user's own example was "0-10, 20-100", i.e. ranges. Ranges are the wrong
 * primitive here and it is worth being explicit about why, because "ranges" is what
 * anyone would reach for first:
 *
 *  - a range list can have GAPS (what colour is 15, in `0-10` and `20-100`?) and
 *    OVERLAPS (which colour wins?). Both are authoring mistakes with no good
 *    runtime answer, and both are unrepresentable with floors.
 *  - a range list needs an upper bound on the last band, so the author has to
 *    predict the maximum. `from: 100` means "100 and up" and never needs revising.
 *
 * So the author still *thinks* in ranges — the inspector shows "10 – 99" beside a
 * swatch, computed from the next floor — while the stored shape cannot express an
 * inconsistent one. The band ends where the next begins; the last runs to infinity.
 *
 * ## Where it lives
 *
 * On `widgetComponentProps`, which is `additionalProperties: true` — the same home
 * `kpi.thresholds` uses, and for the same reason: this is a PRESENTATION rule for
 * one widget, not new grammar. No schema change, so no migration and no risk to a
 * persisted layout. (See `kpiData.ts` § "A middle band can be added later WITHOUT a
 * schema change".)
 *
 * ⚠️ The colour strings are validated with `isCustomTint`, the SAME guard the card
 * tint uses, and for the same reason: these reach a CSS custom property on a SHARED
 * document. A step whose colour fails the guard is DROPPED rather than passed
 * through — see {@link parseColorSteps}.
 */

import { isCustomTint } from '../../WidgetCard/tintTokens';

/** One band: everything from `from` (inclusive) up to the next step's `from`. */
export type ColorStep = {
  /** The band's floor, inclusive. */
  from: number;
  /** `#rrggbb` or `#rrggbbaa`, validated by `isCustomTint`. */
  color: string;
};

const isColorStep = ( raw: unknown ): raw is ColorStep => {
  if ( typeof raw !== 'object' || raw === null ) return false;
  const candidate = raw as Record<string, unknown>;
  return typeof candidate.from === 'number'
    && Number.isFinite( candidate.from )
    && isCustomTint( candidate.color );
};

/**
 * Read a step list off untrusted widget props.
 *
 * DROPS malformed entries rather than rejecting the whole list, and this asymmetry
 * is deliberate: a layout is a shared document that can predate any version of this
 * code, so one bad step must not blank a tile that has three good ones. A list that
 * ends up empty resolves to `undefined`, which every caller treats as "no step
 * colouring" — the safe, visible-but-uncoloured state.
 *
 * SORTED ASCENDING on the way out, so {@link resolveStepColor} can scan without
 * re-sorting and so the inspector can render bands in reading order. Authoring order
 * is not meaningful and must not be load-bearing.
 */
export const parseColorSteps = ( raw: unknown ): ColorStep[] | undefined => {
  if ( !Array.isArray( raw )) return undefined;
  const steps = raw.filter( isColorStep ).map(( step ) => ({ from: step.from, color: step.color }));
  if ( steps.length === 0 ) return undefined;
  return steps.sort(( a, b ) => a.from - b.from );
};

/**
 * The colour for a value: the LAST step whose floor it clears.
 *
 * Returns `undefined` — not a fallback colour — in three cases, all of which mean
 * "this module has nothing to say, use the widget's normal treatment":
 *
 *  - no steps configured;
 *  - the value is not a finite number (an unmeasured tile is not "in a band", and
 *    painting it the lowest band's colour would be a fabricated reading — the same
 *    rule `valueTone` follows for a `null` KPI);
 *  - the value is BELOW every floor. An author who starts their first band at 10 has
 *    said nothing about 3, and inventing a colour for it would put a number in a
 *    band its author did not define.
 */
export const resolveStepColor = (
  value: number | null | undefined,
  steps: ColorStep[] | undefined
): string | undefined => {
  if ( !steps || steps.length === 0 ) return undefined;
  if ( value === null || value === undefined || !Number.isFinite( value )) return undefined;

  let match: string | undefined;
  // Ascending, so the last floor cleared is the tightest band containing `value`.
  for ( const step of steps ) {
    if ( value >= step.from ) match = step.color;
    else break;
  }
  return match;
};

/**
 * The human label for a band — "10 – 99", or "100 +" for the open-ended last one.
 *
 * Lives here rather than in the inspector so the ranges the author READS are derived
 * from the same array the renderer SCANS. Two implementations of "where does this
 * band end" would be one implementation and one lie.
 *
 * The upper bound is the next floor minus one, which is right for the counts these
 * bands are built for (rows, orders, messages — all integers). It is stated rather
 * than hidden because it IS an assumption: a step list over a fractional measure
 * would label `0 – 9` where the band actually ends at 9.999…, and if step colours
 * are ever offered on a percentage this is the line that needs revisiting.
 */
export const colorStepRangeLabel = (
  steps: ColorStep[],
  index: number,
  openEndedSuffix = '+'
): string => {
  const from = steps[index].from;
  const next = steps[index + 1];
  return next === undefined ? `${ from } ${ openEndedSuffix }` : `${ from } – ${ next.from - 1 }`;
};
